import { useTranslation } from "react-i18next";
import { isErrorWithMessage } from "api/helpers";
import { useSnackbar } from "@mindlercare/ui-web";
import { useMindlerApi } from "api/apiv2";
import { useFlag } from "@mindlercare/unleash-client";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation } = ReactQueryV5;
import { GenericMessageModal } from "./GenericMessageModal";
import { apiv2QueryClient } from "../../../apiv2QueryClient";
import { getPatientMessagesQueryKey } from "hooks/messages";

type Props = {
  onClose: () => void;
  open: boolean;
  messageId: string | null;
  patientUserId: number;
};

export const DeleteMessageModal = ({
  onClose,
  open,
  messageId,
  patientUserId,
}: Props) => {
  const isDeleteMessageEnabled = useFlag("PSYCHOLOGIST_DELETE_MESSAGES");

  const apiv2 = useMindlerApi();

  const { mutateAsync: deleteMessage, isPending } = useMutation(
    apiv2.messages.deletePsychologistMessage.mutationOptions({
      onSuccess: async () => {
        apiv2QueryClient.invalidateQueries({
          queryKey: getPatientMessagesQueryKey(patientUserId),
        });
      },
    }),
  );

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleDelete = async () => {
    try {
      if (!messageId || !isDeleteMessageEnabled) return;

      await deleteMessage({
        messageUuid: messageId,
        patientUserId,
      });

      enqueueSnackbar({
        text: t("DELETE_MESSAGE_PROMPT_SUCCESS", { count: 1 }),
        isError: false,
      });
    } catch (error) {
      if (isErrorWithMessage(error, "Not Authorized")) {
        enqueueSnackbar({
          text: t("NOT_PATIENT_DELETE_MESSAGE_ERROR"),
          isError: true,
        });
      } else {
        enqueueSnackbar({
          text: t("SOMETHING_WENT_WRONG"),
          isError: true,
        });
      }
    }

    onClose();
  };

  return (
    <GenericMessageModal
      onClose={onClose}
      open={open}
      title={t("DELETE_MESSAGE_PROMPT")}
      onConfirm={handleDelete}
      isLoading={isPending}
    />
  );
};
